import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';

const toLocalInput = (iso) => {
  if (!iso) return '';
  // Backend returns dates already converted to the tenant timezone as wall-clock
  // ISO strings (no 'Z' suffix). Slice the string directly to feed datetime-local
  // without round-tripping through the browser's local timezone, which would
  // shift the displayed time when the browser TZ differs from the tenant TZ.
  if (typeof iso === 'string') return iso.slice(0, 16);
  const d = iso;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const SocialEventFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = !!id;

  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);

  const [form, setForm] = useState({
    title: '',
    description: '',
    location: '',
    start: '',
    end: '',
    leagueId: '',
    teamId: '',
  });

  const loadLeagues = useCallback(async () => {
    try {
      const data = await unifiedApi.getLeagues();
      setLeagues(data || []);
    } catch {
      // Non-fatal: form still works for tenant-wide events.
    }
  }, []);

  const loadTeams = useCallback(async (leagueId) => {
    if (!leagueId) {
      setTeams([]);
      return;
    }
    try {
      const data = await unifiedApi.getLeagueTeamsWithManagers(leagueId);
      setTeams(data || []);
    } catch {
      setTeams([]);
    }
  }, []);

  useEffect(() => { loadLeagues(); }, [loadLeagues]);

  useEffect(() => {
    if (!editing) return;
    (async () => {
      setLoading(true);
      try {
        const ev = await unifiedApi.getSocialEvent(id);
        if (!ev) {
          setError('Event not found.');
          return;
        }
        setForm({
          title: ev.title,
          description: ev.description || '',
          location: ev.location || '',
          start: toLocalInput(ev.start),
          end: toLocalInput(ev.end),
          leagueId: ev.leagueId ?? '',
          teamId: ev.teamId ?? '',
        });
        if (ev.leagueId) await loadTeams(ev.leagueId);
      } catch {
        setError('Failed to load event.');
      } finally {
        setLoading(false);
      }
    })();
  }, [editing, id, loadTeams]);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onLeagueChange = (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, leagueId: val, teamId: '' }));
    if (val) loadTeams(val); else setTeams([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // Send the wall-clock string from the datetime-local input directly.
      // The backend treats incoming dates as tenant-local and converts to UTC.
      // Do NOT use Date#toISOString — it would emit a UTC 'Z' value and cause
      // a double timezone shift on the server.
      const payload = {
        title: form.title.trim(),
        description: form.description?.trim() || null,
        location: form.location?.trim() || null,
        start: form.start,
        end: form.end || null,
        leagueId: form.leagueId || null,
        teamId: form.teamId || null,
        isLeagueWide: !form.teamId,
      };
      if (editing) {
        // Update payload differs slightly: ClearTeamId sentinel.
        const updatePayload = {
          ...payload,
          clearTeamId: !form.teamId,
          teamId: form.teamId || null,
        };
        delete updatePayload.leagueId; // league cannot be changed after creation.
        await unifiedApi.updateSocialEvent(id, updatePayload);
      } else {
        await unifiedApi.createSocialEvent(payload);
      }
      navigate('/admin/social-events');
    } catch (err) {
      setError(err?.message || 'Failed to save event.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {editing ? 'Edit Social Event' : 'Create Social Event'}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2} component="form" onSubmit={handleSubmit}>
          <TextField
            label="Title"
            value={form.title}
            onChange={update('title')}
            required
            fullWidth
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={update('description')}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="Location"
            value={form.location}
            onChange={update('location')}
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Start"
              type="datetime-local"
              value={form.start}
              onChange={update('start')}
              required
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End (optional)"
              type="datetime-local"
              value={form.end}
              onChange={update('end')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>

          <FormControl fullWidth disabled={editing}>
            <InputLabel id="league-label">Scope</InputLabel>
            <Select
              labelId="league-label"
              label="Scope"
              value={form.leagueId === '' ? '' : form.leagueId}
              onChange={onLeagueChange}
            >
              <MenuItem value=""><em>Tenant-wide</em></MenuItem>
              {leagues.map((l) => (
                <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {form.leagueId && (
            <FormControl fullWidth>
              <InputLabel id="team-label">Team (optional)</InputLabel>
              <Select
                labelId="team-label"
                label="Team (optional)"
                value={form.teamId === '' ? '' : form.teamId}
                onChange={(e) => setForm((f) => ({ ...f, teamId: e.target.value }))}
              >
                <MenuItem value=""><em>All teams in league</em></MenuItem>
                {teams.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={() => navigate('/admin/social-events')} disabled={saving}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving || !form.title || !form.start}>
              {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </AdminLayout>
  );
};

export default SocialEventFormPage;
